/* tslint:disable:only-arrow-functions no-console no-conditional-assignment */

import { logMessage } from './sentry';
import { templateMacros } from './url-utils';
import { replacerWithoutPattern } from './utils';
import { DebugLog } from './debug-log';

interface ScriptAttribute {
  name: string;
  value: string;
}

interface Options {
  overloadDocumentWrite: boolean;
  enableSentryLog: boolean;
  allowInjectIntoBody: boolean;
}

/**
 * @param code is a string containing all code to inject
 * @param htmlContainerSelector string is the CSS selector where HTML will be injected
 * @param scriptsContainerSelector string is the CSS selector where script will be injected
 * @param options list of Options
 */

export function injectTags(
  code: string,
  htmlContainerSelector: string,
  scriptsContainerSelector: string,
  fallbackContainerSelector: string,
  options: Options
) {
  if (options.overloadDocumentWrite) {
    overloadDocumentWrite(
      htmlContainerSelector,
      scriptsContainerSelector,
      fallbackContainerSelector,
      options
    );
  }

  const codeToInject = new CodeManager(code)
    .replaceMacros()
    .cleanSpacesBetweenTags()
    .cleanHTML()
    .removeTags('noscript');

  injectNonScriptTags(
    codeToInject.extractCodeToInject(),
    htmlContainerSelector,
    fallbackContainerSelector,
    options.allowInjectIntoBody
  );
  injectScriptTags(
    codeToInject.extractScriptTags(),
    scriptsContainerSelector,
    fallbackContainerSelector
  );
}

function overloadDocumentWrite(
  nonScriptContainerRef: string,
  scriptsContainerRef: string,
  fallbackContainerSelector: string,
  options: Options
) {
  window.document.writeln = window.document.write = function() {
    const args: string = Array.prototype.slice
      .call(arguments)
      .join('')
      .trim();
    DebugLog.log('document.write called with: ', { args });
    if (options.enableSentryLog) {
      if (args.charAt(0) !== '<') {
        logMessage(
          'An injected code (display tracker or mraid tag) uses "document.write" method for this code',
          {
            code: args
          }
        );
      }
    }

    injectTags(
      args,
      nonScriptContainerRef,
      scriptsContainerRef,
      fallbackContainerSelector,
      {
        ...options,
        overloadDocumentWrite: false
      }
    );
  };
}

function injectNonScriptTags(
  code: string,
  containerSelector: string,
  defaultContainerSelector: string,
  allowedInBody: boolean = false
) {
  const container =
    document.querySelector(containerSelector) ||
    document.querySelector(defaultContainerSelector);
  if (!allowedInBody && container === document.body) {
    console.error(
      'Try to wipe body with display trackers. display trackers injection stops'
    );
  } else if (code) {
    // WARNING: a script display trackers may found this images instead of the real creative
    // for debug try to set dt_force_html_container to '#root'
    const subContainer = document.createElement('div');
    subContainer.style.width = '100%';
    subContainer.style.height = '100%';
    subContainer.innerHTML = code;
    container!.appendChild(subContainer);
  }
}

function injectScriptTags(
  scriptTags: ScriptTag[],
  containerSelector: string,
  defaultContainerSelector: string
) {
  const tryParent =
    defaultContainerSelector === containerSelector &&
    containerSelector !== 'body';

  let container = document.querySelector(containerSelector);
  if (container && tryParent) {
    container = container.parentElement;
  }
  if (!container) {
    container = document.querySelector(defaultContainerSelector);
  }

  scriptTags.forEach((scriptTag: ScriptTag) => {
    if (scriptTag.delay) {
      setTimeout(() => {
        if (container) {
          container.appendChild(scriptTag.toDomElement());
        }
      }, scriptTag.delay);
    } else if (container) {
      container.appendChild(scriptTag.toDomElement());
    }
  });
}

/**
 * @class CodeManager
 * Manage the code string to inject
 */
export class CodeManager {
  public code: string;

  constructor(code: string) {
    this.code = code;
  }

  public cleanExtraMraidTag() {
    const langJs = '( language="javascript"){0,1}';
    const typeJs = '( type="text/javascript"){0,1}';
    return new CodeManager(
      replacerWithoutPattern(
        this.cleanSpacesBetweenTags().code,
        new RegExp(
          `<script${langJs}${typeJs}${langJs} src="mraid.js"${langJs}${typeJs}${langJs}></script>`,
          'gi'
        ),
        ''
      )
    );
  }

  public cleanCeltraContainerScript() {
    const script = replacerWithoutPattern(
      this.cleanSpacesBetweenTags().code,
      '<script type="text/adtag">',
      ''
    );
    return new CodeManager(
      script.replace(new RegExp('</([a-zA-Z]+)></script>', 'i'), '</$1>')
    );
  }

  public cleanHTML(): CodeManager {
    return new CodeManager(
      replacerWithoutPattern(this.code, /><\/IMG>/gi, '/>')
    );
  }

  public cleanSpacesBetweenTags(): CodeManager {
    return new CodeManager(
      replacerWithoutPattern(this.code, />[\s]+</gm, '><').trim()
    );
  }

  public replaceMacros(): CodeManager {
    const macros = window.mraid ? window.mraid.getMaxSize() : {};
    return new CodeManager(templateMacros(this.code, macros));
  }

  public removeTags(tagName: string): CodeManager {
    const re = this.getTagSelector(tagName);
    return new CodeManager(replacerWithoutPattern(this.code, re, ''));
  }

  public extractCodeToInject(): string {
    return this.removeTags('script').code.trim();
  }

  public extractScriptTags(): ScriptTag[] {
    const re = this.getTagSelector('script');
    const tagsContents: ScriptTag[] = [];
    let match;
    while ((match = re.exec(this.code)) !== null) {
      const code = match[2] || '';
      const attributes = match[1] || '';
      tagsContents.push(new ScriptTag(code, attributes));
    }
    return tagsContents;
  }

  public getTagSelector(tag: string): RegExp {
    // RegExp : all ${code} tag with actual content and potential attributes non greedy
    // group1 : attributes / group2 : tag innerHTML
    // work with : https://regex101.com/r/Ms1owH/2
    return new RegExp(
      // eslint-disable-next-line
      `<${tag}([^>]*)>((?!<\\\/${tag}>)[\\\s\\\S]*?)?<\\\/${tag}>`,
      'gmi'
    );
  }
}

/**
 * @class ScriptTag
 * Manage a script tag
 */
export class ScriptTag {
  public content: string;
  public attributes: ScriptAttribute[];
  public delay: number;

  /**
   * @param content is a string containing the code inside <script> tag
   * @param stringAttributes is a string with attribute like 'key="value" key2="value2" ...'
   */
  constructor(content: string, stringAttributes: string = '') {
    this.content = content.trim();
    this.delay = 0;
    this.attributes = this.parseAttributes(stringAttributes);
  }

  public toDomElement(): HTMLElement {
    const newScriptTag = document.createElement('script');
    newScriptTag.type = 'text/javascript';
    this.attributes.forEach((attribute: ScriptAttribute) => {
      newScriptTag.setAttribute(attribute.name, attribute.value);
    });
    newScriptTag.textContent = this.content;
    return newScriptTag;
  }

  public parseAttributes(stringAttributes: string): ScriptAttribute[] {
    const rawAttributes = this.cleanAttributes(
      this.preserveAsync(stringAttributes)
    );
    if (!rawAttributes) {
      return [];
    }
    // RegExp : html attribute parser
    // group1 : attribut name / group2 : attribute value / group3 : attribute value when no quotes
    // work with : https://regex101.com/r/yyxf2Q/1
    const re = new RegExp(
      /([^= ]+)=(?:["']((?:.(?!["']?\s+(?:\S+)=|[>]))+.)["']|(\w+))\s/gim
    );
    const attributes: ScriptAttribute[] = [];
    // eslint-disable-next-line
    let urlAttributeAsLast: ScriptAttribute | void;
    let match;
    // add ending space to fully parse attributs with js in it
    while ((match = re.exec(rawAttributes + ' ')) !== null) {
      const name = match[1];
      const value = match[2] || match[3];
      if (name && value) {
        if (name === 'url') {
          urlAttributeAsLast = { name, value };
        } else if (name === 'delay') {
          this.delay = parseInt(value);
        } else {
          attributes.push({ name, value });
        }
      }
    }
    if (urlAttributeAsLast) {
      attributes.push(urlAttributeAsLast);
    }
    return attributes;
  }

  public preserveAsync(attributes: string): string {
    return ['async', 'defer'].reduce((current, attr) => {
      return current.replace(
        new RegExp(` ${attr}([^=])`, 'i'),
        ` ${attr}="${attr}" $1`
      );
    }, attributes);
  }

  public cleanAttributes(attributes: string): string {
    // remove multiple whitespace
    attributes = replacerWithoutPattern(attributes, /\s+/gim, ' ');
    // remove spaces after "=" allowed in HTML spec.
    attributes = replacerWithoutPattern(attributes, /=\s+/gim, '=');
    return attributes.trim();
  }
}
